<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import AlphaColor from './alphaColor'

type TStarProps = {
  fill: number
  points: any[]
  size: number
  starId: number
  activeColor: string
  inactiveColor: string
  borderColor: string
  activeBorderColor: string
  borderWidth: number
  roundedCorners: boolean
  rtl: boolean
  glow: number
  glowColor: string | null
  animate: boolean
}

const props = withDefaults(defineProps<TStarProps>(), {
  fill: 0,
  points: () => [],
  size: 50,
  borderColor: '#000',
  activeBorderColor: '#000',
  borderWidth: 0,
  roundedCorners: false,
  rtl: false,
  glow: 0,
  glowColor: null,
  animate: false,
})

const emit = defineEmits<{
  (e: 'starMouseMove', payload: { event: Event; position: number; id: number }): void
  (e: 'starSelected', payload: { id: number; position: number }): void
}>()

const starPoints = ref([19.8, 2.2, 6.6, 43.56, 39.6, 17.16, 0, 17.16, 33, 43.56])
const grad = ref('')
const glowId = ref('')
const isStarActive = ref(true)

const starPointsToString = computed(() => starPoints.value.join(','))

const gradId = computed(() => `url(#${grad.value})`)

const border = computed(() => {
  return (props.roundedCorners && props.borderWidth <= 0) ? 6 : props.borderWidth
})

const starSize = computed(() => {
  const size = (props.roundedCorners && props.borderWidth <= 0) ? props.size - border.value : props.size
  return size + border.value
})

const starFill = computed(() => {
  return (props.rtl) ? `${100 - props.fill}%` : `${props.fill}%`
})

const getBorderColor = computed(() => {
  if (props.roundedCorners && props.borderWidth <= 0)
    return (props.fill <= 0) ? props.inactiveColor : props.activeColor

  return (props.fill <= 0) ? props.borderColor : props.activeBorderColor
})

const maxSize = computed(() => {
  return starPoints.value.reduce((a, b) => Math.max(a, b))
})

const viewBox = computed(() => {
  return `0 0 ${maxSize.value} ${maxSize.value}`
})

const shouldAnimate = computed(() => {
  return props.animate && isStarActive.value
})

const strokeLinejoin = computed(() => {
  return props.roundedCorners ? 'round' : 'miter'
})

function getRandomId() {
  return Math.random().toString(36).substring(7)
}

function calculatePoints() {
  starPoints.value = starPoints.value.map((point, i) => {
    const offset = i % 2 === 0 ? border.value * 1.5 : 0
    return ((props.size / maxSize.value) * point) + offset
  })
}

onMounted(() => {
  starPoints.value = (props.points && props.points.length) ? props.points : starPoints.value
  calculatePoints()
  grad.value = getRandomId()
  glowId.value = getRandomId()
})

const mouseMoving = useDebounceFn((event) => {
  emit('starMouseMove', {
    event,
    position: getPosition(event),
    id: props.starId,
  })
}, 10)

function touchStart() {
  isStarActive.value = true
}

function touchEnd() {
  isStarActive.value = false
}

function getPosition(event) {
  const starWidth = (92 / 100) * props.size
  const offset = props.rtl ? Math.min(event.offsetX, 45) : Math.max(event.offsetX, 1)
  const position = Math.round((100 / starWidth) * offset)

  return Math.min(position, 100)
}

function selected(event) {
  emit('starSelected', {
    id: props.starId,
    position: getPosition(event),
  })
}

const getColor = (color: string) => new AlphaColor(color).parseAlphaColor().color

const getOpacity = (color: string) => new AlphaColor(color).parseAlphaColor().opacity
</script>

<template>
  <svg
    class="vue-star-rating-star" :class="[{ 'vue-star-rating-star-rotate': shouldAnimate }]"
    :height="starSize"
    :width="starSize"
    :viewBox="viewBox"
    @mousemove="mouseMoving"
    @click="selected"
    @touchstart="touchStart"
    @touchend="touchEnd"
  >

    <linearGradient
      :id="grad"
      x1="0"
      x2="100%"
      y1="0"
      y2="0"
    >
      <stop
        :offset="starFill"
        :stop-color="(rtl) ? getColor(inactiveColor) : getColor(activeColor)"
        :stop-opacity="(rtl) ? getOpacity(inactiveColor) : getOpacity(activeColor)"
      />
      <stop
        :offset="starFill"
        :stop-color="(rtl) ? getColor(activeColor) : getColor(inactiveColor)"
        :stop-opacity="(rtl) ? getOpacity(activeColor) : getOpacity(inactiveColor)"
      />
    </linearGradient>

    <filter
      :id="glowId"
      height="130%"
      width="130%"
      filterUnits="userSpaceOnUse"
    >
      <feGaussianBlur
        :stdDeviation="glow"
        result="coloredBlur"
      />
      <feMerge>
        <feMergeNode in="coloredBlur" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>

    <polygon
      v-show="glowColor && glow > 0 && fill > 0"
      :points="starPointsToString"
      :fill="gradId"
      :stroke="glowColor"
      :filter="`url(#${glowId})`"
      :stroke-width="border"
    />

    <polygon
      :points="starPointsToString"
      :fill="gradId"
      :stroke="getBorderColor"
      :stroke-width="border"
      :stroke-linejoin="strokeLinejoin"
    />
    <polygon
      :points="starPointsToString"
      :fill="gradId"
    />
  </svg>
</template>

<style scoped>
    .vue-star-rating-star {
        overflow: visible !important;
    }

    .vue-star-rating-star-rotate {
        transition: all ease .25s;
    }

    .vue-star-rating-star-rotate:hover {
        transition: transform 0.25s;
        transform: scale(1.3)
    }
</style>
